import { AdDismissedError, AdTooSoonError, AdUnavailableError } from './ads.js';

/**
 * USL's TowerAds SDK (uslads.com), for the task centre's USL slot.
 *
 * As USL's integration has it: `new TowerAds({ apiKey, placementId,
 * onRewardEarned, onError })`, then `await ads.loadAndShow()` for each ad, the
 * reward being whatever `onRewardEarned` is called with. An ad counts only
 * when it was called. USL publishes nothing more about its SDK, so when an ad
 * is over is read from the screen (`playUsl`).
 *
 * One TowerAds is made per key and placement as soon as the Tasks page knows
 * the slot is on (`prepareUsl`), its script fetched then, and kept; its
 * callbacks speak to the ad being played.
 */

interface TowerAds {
  loadAndShow(): Promise<unknown>;
}

type TowerAdsClass = new (options: {
  apiKey: string;
  placementId: string;
  onRewardEarned: (reward: unknown) => void;
  onError: (error: unknown) => void;
}) => TowerAds;

const SCRIPT_URL = 'https://uslads.com/sdk/tower-ads-v4.js';
const LOAD_TIMEOUT_MS = 15_000;
/** With the ad gone and no reward yet, how long USL's reward may still come. */
const REWARD_GRACE_MS = 1_500;
/** Nothing on the page and no answer: how long to wait for the player to come back before giving up. */
const UNSEEN_TIMEOUT_MS = 60_000;
/** How long one ad may take, loading, playing and closing, before it is given up. */
const PLAY_TIMEOUT_MS = 5 * 60_000;

/**
 * The SDK's class. A script's top-level `class TowerAds` is a global every
 * script can name but not a property of `window`, so it is looked up both
 * ways (the Mini App's policy allows `Function`).
 */
function towerAdsClass(): TowerAdsClass | undefined {
  const own = (window as unknown as { TowerAds?: unknown }).TowerAds;
  if (typeof own === 'function') return own as TowerAdsClass;
  try {
    const named = new Function('return typeof TowerAds === "function" ? TowerAds : undefined')() as unknown;
    return typeof named === 'function' ? (named as TowerAdsClass) : undefined;
  } catch {
    return undefined;
  }
}

let loading: Promise<void> | null = null;

/** USL's script, once per page; rejects with AdUnavailableError when it cannot be had. */
export function loadUsl(): Promise<void> {
  if (towerAdsClass()) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    const done = (error?: AdUnavailableError) => {
      window.clearTimeout(timer);
      if (!error) return resolve();
      script.remove();
      loading = null;
      reject(error);
    };
    const timer = window.setTimeout(() => done(new AdUnavailableError('USL took too long to load.', 'unreachable')), LOAD_TIMEOUT_MS);
    script.onload = () => done(towerAdsClass() ? undefined : new AdUnavailableError('USL did not load.', 'notLoaded'));
    script.onerror = () => done(new AdUnavailableError('USL could not be reached.', 'unreachable'));
    script.src = SCRIPT_URL;
    script.async = true;
    document.head.appendChild(script);
  });
  return loading;
}

/** The ad being played: the SDK's callbacks, set once per TowerAds, speak to it. */
interface Play {
  rewarded(reward: unknown): void;
  failed(error: unknown): void;
}

const towers = new Map<string, TowerAds>();
let playing: Play | null = null;

function towerFor(apiKey: string, placementId: string): TowerAds {
  const key = `${apiKey}|${placementId}`;
  const known = towers.get(key);
  if (known) return known;
  const Tower = towerAdsClass();
  if (!Tower) throw new AdUnavailableError('USL did not load.', 'notLoaded');
  let tower: TowerAds;
  try {
    tower = new Tower({
      apiKey,
      placementId,
      onRewardEarned: (reward) => playing?.rewarded(reward),
      onError: (error) => playing?.failed(error),
    });
  } catch (error) {
    throw uslFailure(error);
  }
  towers.set(key, tower);
  return tower;
}

/** The TowerAds made ahead of the ad, its script with it; quiet when it cannot be. */
export async function prepareUsl(apiKey: string, placementId: string): Promise<void> {
  if (!apiKey || !placementId || towers.has(`${apiKey}|${placementId}`)) return;
  await loadUsl();
  towerFor(apiKey, placementId);
}

/** What USL's error says, as the app's own ad errors, which say it in the reader's language. */
export function uslFailure(error: unknown): Error {
  const said =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : typeof error === 'object' && error !== null && 'message' in error
          ? String((error as { message: unknown }).message)
          : '';
  if (/no.?(ads?|fill)|not available|unavailable|no_ads|inventory|empty/i.test(said)) {
    return new AdUnavailableError('No USL ad to show right now.', 'noFill');
  }
  if (/already|in progress|too (many|soon|fast|often)|rate.?limit|frequency|cooldown/i.test(said)) return new AdTooSoonError();
  if (/clos|skip|cancel|dismiss/i.test(said)) return new AdDismissedError();
  return new AdUnavailableError(said);
}

export interface UslOutcome {
  /** What USL handed `onRewardEarned`, as it came. */
  reward: unknown;
}

/** Whether something other than the app is over the middle of the screen: an ad, then. */
function covered(root: HTMLElement | null): boolean {
  if (!root || typeof document.elementFromPoint !== 'function') return false;
  const top = document.elementFromPoint(window.innerWidth / 2, window.innerHeight / 2);
  return top !== null && !root.contains(top);
}

/** How often the screen is looked at while an ad plays. */
const LOOK_MS = 250;

/**
 * Plays one USL rewarded ad. Resolves with its reward once USL said it was
 * earned and the ad is over; rejects with AdDismissedError when the ad was
 * closed without it, and with the app's other ad errors when there was no ad
 * to show or USL failed.
 *
 * The ad is over when `loadAndShow` has settled and nothing covers the app any
 * more — or when what covered it went away, whatever `loadAndShow` does. An ad
 * that never covered the app (shown some other way) is over when the player
 * touches the app again.
 */
export async function playUsl(apiKey: string, placementId: string): Promise<UslOutcome> {
  if (!apiKey || !placementId) throw new AdUnavailableError('The ad session is missing its USL placement.', 'noBlock');
  await loadUsl();
  const tower = towerFor(apiKey, placementId);
  if (playing) throw new AdTooSoonError();

  return await new Promise<UslOutcome>((resolve, reject) => {
    const root = document.getElementById('root');
    let reward: { value: unknown } | null = null;
    let failure: Error | null = null;
    let settled = false;
    let seen = false;
    let over = false;
    let grace: number | undefined;
    let unseen: number | undefined;

    const finish = (settle: () => void) => {
      if (over) return;
      over = true;
      window.clearInterval(look);
      window.clearTimeout(grace);
      window.clearTimeout(unseen);
      window.clearTimeout(cap);
      document.removeEventListener('pointerdown', onTouch, true);
      playing = null;
      settle();
    };
    const earned = () => finish(() => resolve({ reward: reward?.value }));
    const closedWithout = () => finish(() => reject(failure ?? new AdDismissedError()));
    const end = () => (reward ? earned() : closedWithout());

    /** Whether the ad is over, and how it went. */
    const decide = () => {
      if (over || !settled || covered(root)) return;
      if (reward) return earned();
      if (failure) return closedWithout();
      if (seen) {
        // It came and went: the reward may land a moment after the close.
        grace ??= window.setTimeout(() => {
          grace = undefined;
          if (!covered(root)) end();
        }, REWARD_GRACE_MS);
      } else {
        // Never over the app: it is over when the player is back in it.
        unseen ??= window.setTimeout(() => {
          unseen = undefined;
          if (!covered(root)) end();
        }, UNSEEN_TIMEOUT_MS);
      }
    };

    const look = window.setInterval(() => {
      if (covered(root)) {
        seen = true;
        return;
      }
      // What covered the app is gone: the ad is over, whatever loadAndShow does.
      if (seen) settled = true;
      decide();
    }, LOOK_MS);

    const onTouch = (event: Event) => {
      if (!root?.contains(event.target as Node)) return;
      // Before anything showed and before USL answered, a touch says nothing: it may be loading still.
      if (!settled && !seen && !reward) return;
      settled = true;
      end();
    };
    document.addEventListener('pointerdown', onTouch, true);

    const cap = window.setTimeout(end, PLAY_TIMEOUT_MS);

    playing = {
      rewarded: (value) => {
        reward = { value };
        // Earned with nothing seen over the app: shown some other way, and done.
        if (!seen) settled = true;
        decide();
      },
      failed: (error) => {
        // An error with an ad up is not the end of it; one before any is.
        failure ??= uslFailure(error);
        window.setTimeout(() => {
          if (!seen) settled = true;
          decide();
        }, REWARD_GRACE_MS);
      },
    };

    let shown: Promise<unknown>;
    try {
      shown = Promise.resolve(tower.loadAndShow());
    } catch (error) {
      failure = uslFailure(error);
      return closedWithout();
    }
    shown.then(
      () => {
        settled = true;
        decide();
      },
      (error: unknown) => {
        failure ??= uslFailure(error);
        settled = true;
        decide();
      },
    );
  });
}
